import { onActivated, onDeactivated, onScopeDispose, watch } from 'vue';
import { createPollServerBackoff, isPollServerError } from '@/utils/poll-server-backoff.js';

const DEFAULT_INTERVAL_MS = 5000;
const RESYNC_DEBOUNCE_MS = 300;

/**
 * 通用文档级轮询（visibility 切换时暂停；5xx 自动降频，成功恢复）。
 * 市场详情页请使用 `useMarketDetailPoll`，勿直接调用本函数。
 *
 * @param {() => void | Promise<void>} callback
 * @param {{
 *   intervalMs?: number | (() => number),
 *   enabled?: () => boolean,
 *   watchSources?: import('vue').WatchSource | import('vue').WatchSource[],
 *   scope?: string,
 *   skipIfBusy?: boolean,
 * }} [options]
 */
export function useDocumentPoll(callback, options = {}) {
  function resolveConfiguredIntervalMs() {
    const raw =
      typeof options.intervalMs === 'function' ? options.intervalMs() : options.intervalMs;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_INTERVAL_MS;
  }

  const enabled = options.enabled ?? (() => true);
  const scopeLabel = options.scope ?? 'default';
  const skipIfBusy = options.skipIfBusy === true;

  const backoff = createPollServerBackoff({
    baseIntervalMs: resolveConfiguredIntervalMs(),
    getBaseIntervalMs: resolveConfiguredIntervalMs,
  });

  let timerId = null;
  let resyncTimer = null;
  let running = false;
  let tickInFlight = false;

  function isActive() {
    return running;
  }

  function clearTimer() {
    if (timerId != null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  /** 是否保持定时器存活（与 enabled 解耦：未就绪时仍按间隔重试） */
  function canKeepScheduling() {
    return running && !document.hidden;
  }

  function scheduleNext(delayMs = backoff.getIntervalMs()) {
    clearTimer();
    if (!canKeepScheduling()) return;
    timerId = setTimeout(onTimerFire, delayMs);
  }

  function handleTickSuccess() {
    backoff.recordSuccess();
  }

  function handleTickError(err) {
    if (!isPollServerError(err)) return;
    const prevLevel = backoff.level;
    backoff.recordServerError();
    if (backoff.level !== prevLevel) {
      console.warn(
        `[poll:${scopeLabel}] server error, backoff level ${
          backoff.level
        }, next in ${backoff.getIntervalMs()}ms`,
        err
      );
    }
  }

  async function executeTick() {
    if (!running || document.hidden) return;
    // enabled 未就绪：跳过本拍，由 onTimerFire 继续 scheduleNext
    if (!enabled()) return;
    if (skipIfBusy && tickInFlight) return;

    let result;
    try {
      result = callback();
    } catch (err) {
      console.warn(`[poll:${scopeLabel}] tick callback failed`, err);
      handleTickError(err);
      return;
    }

    if (result == null || typeof result.then !== 'function') {
      handleTickSuccess();
      return;
    }

    if (skipIfBusy) tickInFlight = true;
    try {
      await result;
      handleTickSuccess();
    } catch (err) {
      console.warn(`[poll:${scopeLabel}] tick callback failed`, err);
      handleTickError(err);
    } finally {
      if (skipIfBusy) tickInFlight = false;
    }
  }

  async function onTimerFire() {
    timerId = null;
    await executeTick();
    if (canKeepScheduling()) {
      scheduleNext();
    }
  }

  function tick() {
    if (!running) return;
    clearTimer();
    void onTimerFire();
  }

  function stop() {
    if (!running && timerId == null) return;
    running = false;
    tickInFlight = false;
    backoff.reset();
    clearTimer();
    if (resyncTimer != null) {
      clearTimeout(resyncTimer);
      resyncTimer = null;
    }
  }

  function start() {
    running = true;
    clearTimer();
    void onTimerFire();
  }

  function scheduleResync() {
    if (resyncTimer != null) clearTimeout(resyncTimer);
    resyncTimer = setTimeout(() => {
      resyncTimer = null;
      if (!running) {
        start();
        return;
      }
      clearTimer();
      void onTimerFire();
    }, RESYNC_DEBOUNCE_MS);
  }

  function onVisibilityChange() {
    if (document.hidden) {
      clearTimer();
      return;
    }
    if (!running) return;
    clearTimer();
    void onTimerFire();
  }

  function bindVisibilityListener() {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function unbindVisibilityListener() {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  if (options.watchSources) {
    watch(options.watchSources, scheduleResync, { flush: 'post' });
  }

  bindVisibilityListener();
  start();

  onDeactivated(() => {
    stop();
  });

  onActivated(() => {
    running = true;
    clearTimer();
    void onTimerFire();
  });

  onScopeDispose(() => {
    stop();
    unbindVisibilityListener();
    if (process.env.NODE_ENV !== 'production' && scopeLabel) {
      console.debug(`[poll:${scopeLabel}] disposed`);
    }
  });

  return { start, stop, tick, isActive };
}
